import { usersApi } from "@/api/users.api";
import { Button } from "@/components/generic/Button";
import { Select, SelectItem } from "@/components/generic/Select";
import { TextField } from "@/components/generic/TextField";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { UserRole } from "@/types/user";
import {
  ApiException,
  isGeneralException,
  isLassoException,
  LassoException,
} from "@/utils/api";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  linkOptions,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { HTTPError } from "ky";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/users/_users/invite-new",
)({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    if (context.trialPeriod?.isExpired)
      throw redirect({ from: Route.fullPath, to: ".." });
  },
});

const inputSchema = z.object({
  name: z.string().min(1, { message: "Navn er påkrævet" }),
  email: z.email({ message: "Ugyldig e-mail" }),
  password: z.string().optional(),
  role: z.enum(["Sales", "Salesleader"], {
    error: () => "Rolle er påkrævet",
  }),
});

function RouteComponent() {
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    context: "inviteNewUser",
    resolver: zodResolver(inputSchema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await usersApi.inviteNewUser({ ...data, roles: [data.role, UserRole.User] });

      toast.success("Brugeren oprettet", {
        description: "Vi har sendt en mail med oplysninger til at logge ind",
      });

      navigate({ to: "/settings/users" });
    } catch (err: unknown) {
      console.error(err);
      if (!(err instanceof HTTPError)) throw err;
      const errorData = await err.response.json<
        ApiException | LassoException
      >();
      if (!isGeneralException(errorData) && !isLassoException(errorData)) {
        Object.keys(errorData).forEach((fieldName) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setError(fieldName, {
            type: "server",
            message: errorData[fieldName],
          });
        });
      } else if (isLassoException(errorData)) {
        setError("root", {
          type: "server",
          message: errorData.message,
        });
      } else {
        // Handle unexpected error format
        setError("root", {
          type: "server",
          message: "An unexpected error occurred.",
        });
      }
      clearErrors();
    }
  });

  return (
    <SettingsContainer
      className="mx-14 mt-7 mb-12 h-full"
      backLinkOptions={linkOptions({
        from: Route.fullPath,
        to: "/settings/users",
      })}
    >
      <div className="mx-auto flex flex-col items-center gap-y-10">
        <div className="flex flex-col items-center gap-y-3">
          <h3 className="text-theme-on-background text-base font-medium">
            Inviter ny bruger
          </h3>
          <p className="text-theme-on-background-variant mt-3 text-center text-sm font-medium">
            Brugeren modtager en e-mail med oplysninger til at logge ind
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-2 w-full space-y-6">
          <TextField
            className="mx-auto max-w-[370px]"
            name="name"
            control={control}
            aria-label="Navn"
            placeholder="Navn"
            isRequired
          />
          <TextField
            className="mx-auto max-w-[370px]"
            name="email"
            type="email"
            control={control}
            aria-label="E-mail"
            placeholder="E-mail"
            isRequired
          />

          <div className="my-14 flex flex-col items-center gap-y-4">
            <h3 className="text-theme-on-background text-base font-medium">
              Angiv brugerens rolle
            </h3>
            <p className="text-theme-on-background-variant text-center text-sm leading-[22px]">
              En Salgsleder kan oprette kampagner og administrere
              organisationens løsning
              <br />
              En Sælger kan se leads, men kan ikke oprette eller redigere
              kampagner
            </p>
          </div>

          <Controller
            control={control}
            name="role"
            render={({ field, fieldState }) => (
              <Select
                className="mx-auto max-w-[370px]"
                aria-label="Rolle"
                placeholder="Vælg rolle"
                isRequired
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                items={[
                  { value: "Sales", label: "Sælger" },
                  { value: "Salesleader", label: "Salgsleder" },
                ]}
                errorMessage={fieldState.error?.message}
              >
                {(item) => (
                  <SelectItem aria-label={item.label} id={item.value}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            )}
          />
          <Button
            className="mx-auto block"
            type="submit"
            size="lg"
            intent="primary"
          >
            Inviter bruger
          </Button>
        </form>
      </div>
    </SettingsContainer>
  );
}
